import { useQueryClient } from '@tanstack/react-query'
import { api } from '../lib/api'
import { useTiempoReal } from '../lib/tiempoReal'
import { Puntos } from './Puntos'
import { BotonTema } from './BotonTema'
import { puede, puedeAlgo } from '../lib/permisos'
import type { Usuario } from '../lib/tipos'

/**
 * Menú lateral izquierdo.
 *
 * Estaba arriba, en una franja horizontal, y José la quiere libre para las cosas
 * del contrato que se está gestionando ("aquí lo que quiero son cosas actuales
 * del contrato, tipo tiempos"). Así que el menú se va al lateral y arriba solo
 * queda el cronómetro y los datos de la llamada.
 *
 * Por debajo de `md` no hay sitio para una columna, así que se convierte en una
 * tira horizontal arriba que se desplaza de lado.
 */
export function BarraLateral({
  usuario,
  ruta,
  ir,
}: {
  usuario: Usuario
  ruta: string
  ir: (destino: string) => void
}) {
  const clienteQuery = useQueryClient()
  const { conectado } = useTiempoReal()

  const salir = async () => {
    await api('/api/auth/salir', { metodo: 'POST' })
    await clienteQuery.invalidateQueries({ queryKey: ['yo'] })
  }

  const es = (...roles: string[]) => roles.includes(usuario.rol)

  // La escala de mando de la reunión del 16 de septiembre: cada uno ve lo suyo y
  // lo que tiene por debajo. Backoffice lo puede todo menos métricas.
  // Las otras pantallas, para saber cuándo NO estamos en Contratos, que es la de
  // entrada y se queda con «/» y con las fichas.
  const OTRAS = [
    '/bajas',
    '/oportunidades',
    '/metricas',
    '/sala',
    '/liquidaciones',
    '/mantenimiento',
    '/personal',
  ]

  const enlaces = [
    {
      destino: '/contratos',
      texto: 'Contratos',
      ver: true,
      activo: !OTRAS.some((otra) => ruta.startsWith(otra)),
    },
    // Bajas solo lo ve quien las trabaja: al comercial ya no le aparecen.
    { destino: '/bajas', texto: 'Bajas', ver: puede(usuario, 'contratos.ver_otros') },
    {
      destino: '/oportunidades',
      texto: 'Oportunidades',
      ver: puede(usuario, 'oportunidades'),
    },
    {
      destino: '/metricas',
      // El comercial ve lo suyo, así que para él no son «las métricas».
      texto: es('comercial') ? 'Mi rendimiento' : 'Métricas',
      ver: puede(usuario, 'metricas'),
    },
    { destino: '/sala', texto: 'Sala', ver: puede(usuario, 'sala') },
    { destino: '/liquidaciones', texto: 'Liquidaciones', ver: puede(usuario, 'liquidaciones') },
    { destino: '/mantenimiento', texto: 'Mantenimiento', ver: puede(usuario, 'mantenimiento') },
    { destino: '/personal', texto: 'Personal', ver: puedeAlgo(usuario, 'personal') },
  ].filter((enlace) => enlace.ver)

  return (
    <aside
      className="sticky top-0 z-20 flex shrink-0 flex-col gap-3 border-b border-slate-200 bg-white px-3 py-3
                 md:h-dvh md:w-56 md:border-b-0 md:border-r
                 dark:border-slate-800 dark:bg-slate-900"
    >
      {/* El verde de la palabra es oscuro (luminancia 0.29): en modo oscuro se
          perdería sobre el fondo, así que ahí va sobre pastilla blanca. */}
      <button
        onClick={() => ir('/contratos')}
        className="self-start rounded-lg dark:bg-white dark:px-2 dark:py-1"
        aria-label="Codoh, ir a los contratos"
      >
        <img src="/logo.png" alt="Codoh" className="h-6 w-auto" width={112} height={30} />
      </button>

      <nav className="-mx-1 flex gap-1 overflow-x-auto px-1 md:flex-col md:overflow-visible">
        {enlaces.map((enlace) => (
          <button
            key={enlace.destino}
            onClick={() => ir(enlace.destino)}
            className={`shrink-0 rounded-lg px-3 py-2 text-left text-sm font-medium transition md:w-full ${
              (enlace.activo ?? ruta.startsWith(enlace.destino))
                ? 'bg-marca-50 text-marca-700 dark:bg-marca-700/20 dark:text-marca-100'
                : 'text-slate-500 hover:bg-slate-50 hover:text-slate-800 dark:hover:bg-slate-800 dark:hover:text-slate-200'
            }`}
          >
            {enlace.texto}
          </button>
        ))}
      </nav>

      {/* Abajo del todo en la columna. `items-start` para que la pastilla de
          puntos no se estire a lo ancho y deje de parecer una pastilla. */}
      <div className="flex flex-wrap items-center gap-3 md:mt-auto md:flex-col md:items-start md:gap-2">
        <Puntos usuario={usuario} />

        <span className="flex items-center gap-2">
          <span className="flex items-center gap-1.5 text-xs font-medium text-slate-500">
            <span
              className={`h-2 w-2 shrink-0 rounded-full ${conectado ? 'bg-marca-500' : 'bg-slate-300'}`}
              aria-hidden
            />
            {conectado ? 'En vivo' : 'Sin conexión'}
          </span>
          <BotonTema />
        </span>

        <span className="hidden truncate text-xs text-slate-500 md:block">
          {usuario.nombre}
          <span className="block text-slate-400">{usuario.rol}</span>
        </span>

        <button
          onClick={salir}
          className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm font-medium transition hover:border-red-300 hover:text-red-600 md:w-full dark:border-slate-700"
        >
          Salir
        </button>
      </div>
    </aside>
  )
}
